import { useQuery } from '@tanstack/react-query';
import * as Organization from 'modules/organization';
import * as Collections from 'modules/collections';

/** Filters the Tests list sends to GET /api/tests, shared by the desktop line and the phone sheet. */
export interface TestFilterValue {
  createdById?: string;
  createdFrom?: string;
  createdTo?: string;
  collectionId?: string;
}

export const hasTestFilters = (value: TestFilterValue): boolean =>
  Boolean(value.createdById || value.createdFrom || value.createdTo || value.collectionId);

export const countTestFilters = (value: TestFilterValue): number =>
  (value.createdById ? 1 : 0) + (value.createdFrom || value.createdTo ? 1 : 0) + (value.collectionId ? 1 : 0);

export interface NamedOption {
  id: string;
  name: string;
}

const FIVE_MINUTES = 5 * 60 * 1000;

/** People who can author tests in the org (up to 100), for the author filter. */
export function useTestFilterAuthors(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['test-filter-authors', orgId],
    enabled: Boolean(orgId) && enabled,
    staleTime: FIVE_MINUTES,
    queryFn: async (): Promise<NamedOption[]> => {
      const res = await Organization.Api.GetMembers(orgId as string, {
        role: 'SUPER_ADMIN,ORG_ADMIN,AUTHOR',
        limit: 100,
      });
      const rows =
        (res.data as { data?: Array<{ user?: { id?: string; name?: string } }> })?.data ?? [];
      return rows
        .map((r) => ({ id: r.user?.id ?? '', name: r.user?.name ?? '' }))
        .filter((m) => m.id);
    },
  });
}

/** Collections the user can see, for the collection filter. */
export function useTestFilterCollections(orgId: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: ['test-filter-collections', orgId],
    enabled: Boolean(orgId) && enabled,
    staleTime: FIVE_MINUTES,
    queryFn: async (): Promise<NamedOption[]> => {
      const res = await Collections.Api.list();
      const rows = res.data?.data ?? [];
      return rows.map((c) => ({ id: c.id, name: c.name }));
    },
  });
}
